/**
 * Coreografia do loader da logo — a opção 7B (Giro → Onda) de
 * `docs/design/loader-logo/direction-approved.md`.
 *
 * Mesmo arranjo do `PULSE`: a técnica não atravessa (Web Animations API no
 * web, Reanimated no app), o número sim. Por isso a coreografia sai daqui como
 * trilhas de quadros-chave prontas, e cada plataforma só traduz o formato —
 * se o web e o app montassem os quadros cada um, a deriva voltava.
 *
 * Unidade de deslocamento: a do viewBox das peças (47 × 45), em
 * `LOGO_TAMI_PIECES` de `@tami/ui/brands`. A plataforma converte para px.
 */

/** Curva cúbica de Bézier, `[x1, y1, x2, y2]`. */
export type BezierCurve = readonly [number, number, number, number];

/** As três peças do símbolo. */
export type LogoPiece = "top" | "bottomLeft" | "right";

/** Quadro-chave de transformação. `easing` vale para o trecho que começa nele. */
export type TransformKeyframe = {
  /** Posição no ciclo, de 0 a 1. */
  readonly offset: number;
  readonly x: number;
  readonly y: number;
  readonly scale: number;
  readonly easing: BezierCurve;
};

export type RotateKeyframe = {
  readonly offset: number;
  readonly rotateDeg: number;
  readonly easing: BezierCurve;
};

export type OpacityKeyframe = {
  readonly offset: number;
  readonly opacity: number;
  readonly easing: BezierCurve;
};

export type LogoLoaderTimeline = {
  readonly durationMs: number;
  /**
   * Momentos do ciclo (0 a 1) em que as três peças estão no lugar, paradas e
   * com a cor cheia: o fim do Giro e o fim do ciclo. É onde dá para parar a
   * animação sem cortar movimento — a splash sai num deles.
   */
  readonly restOffsets: readonly number[];
  /** Giro do bloco inteiro, em torno do centro do símbolo. */
  readonly spin: readonly RotateKeyframe[];
  readonly pieces: Readonly<
    Record<
      LogoPiece,
      {
        /** Centro da peça no viewBox — origem da escala dela. */
        readonly origin: readonly [number, number];
        readonly transform: readonly TransformKeyframe[];
        readonly opacity: readonly OpacityKeyframe[];
      }
    >
  >;
};

const LINEAR: BezierCurve = [0, 0, 1, 1];
/** O `ease` do CSS — o que o protótipo usava na opacidade. */
const EASE: BezierCurve = [0.25, 0.1, 0.25, 1];
const SPLIT_OUT: BezierCurve = [0.3, 0, 0.2, 1];
/** Volta com rebote: o y acima de 1 passa do lugar e volta. */
const SNAP_BACK: BezierCurve = [0.3, 1.6, 0.5, 1];
const SPIN: BezierCurve = [0.75, 0, 0.2, 1];
const WAVE: BezierCurve = [0.45, 0, 0.25, 1];

/** Fim da volta das peças e começo da onda, no ciclo de 4 s (2,2 s). */
const WAVE_START = 0.55;
/** Intervalo entre uma peça e a próxima na onda (0,18 s). */
const WAVE_STAGGER = 0.045;
/** Subida (e depois descida) de cada peça na onda (0,36 s). */
const WAVE_RISE = 0.09;
/** Opacidade das peças que não estão no alto durante a onda. */
const DIM_OPACITY = 0.35;

/** Para onde cada peça se afasta pelas ranhuras no Giro. */
const SPLIT_OFFSET: Record<LogoPiece, readonly [number, number]> = {
  top: [-1, -4.5],
  bottomLeft: [-4.5, 3],
  right: [4.5, 2],
};

/** Centro do retângulo que contém cada peça, no viewBox 47 × 45. */
const PIECE_ORIGIN: Record<LogoPiece, readonly [number, number]> = {
  top: [23.51, 9.73],
  bottomLeft: [11.22, 34.21],
  right: [36.77, 29.15],
};

/** Ordem da onda: sentido horário, começando pela base esquerda. */
const WAVE_ORDER: readonly LogoPiece[] = ["bottomLeft", "top", "right"];

/** As peças terminam de voltar ao lugar: fim do Giro (1,85 s). */
const GIRO_END = 0.462;

const SPLIT_SCALE = 0.9;
const LIFT_Y = -3.5;
const LIFT_SCALE = 1.06;

function transformTrack(piece: LogoPiece): TransformKeyframe[] {
  const [sx, sy] = SPLIT_OFFSET[piece];
  const rest = { x: 0, y: 0, scale: 1 };
  const split = { x: sx, y: sy, scale: SPLIT_SCALE };
  const waveAt = WAVE_START + WAVE_ORDER.indexOf(piece) * WAVE_STAGGER;
  return [
    { offset: 0, ...rest, easing: SPLIT_OUT },
    { offset: 0.099, ...split, easing: LINEAR },
    { offset: 0.341, ...split, easing: SNAP_BACK },
    { offset: GIRO_END, ...rest, easing: LINEAR },
    { offset: waveAt, ...rest, easing: WAVE },
    { offset: waveAt + WAVE_RISE, x: 0, y: LIFT_Y, scale: LIFT_SCALE, easing: WAVE },
    { offset: waveAt + 2 * WAVE_RISE, ...rest, easing: LINEAR },
    { offset: 1, ...rest, easing: LINEAR },
  ];
}

function opacityTrack(piece: LogoPiece): OpacityKeyframe[] {
  const waveAt = WAVE_START + WAVE_ORDER.indexOf(piece) * WAVE_STAGGER;
  const frames: OpacityKeyframe[] = [
    { offset: 0, opacity: 1, easing: LINEAR },
    { offset: 0.51, opacity: 1, easing: EASE },
    { offset: WAVE_START, opacity: DIM_OPACITY, easing: EASE },
  ];
  if (waveAt > WAVE_START) frames.push({ offset: waveAt, opacity: DIM_OPACITY, easing: EASE });
  frames.push(
    { offset: waveAt + WAVE_RISE, opacity: 1, easing: EASE },
    { offset: waveAt + 2 * WAVE_RISE, opacity: DIM_OPACITY, easing: EASE },
    { offset: 0.86, opacity: DIM_OPACITY, easing: EASE },
    { offset: 0.94, opacity: 1, easing: LINEAR },
    { offset: 1, opacity: 1, easing: LINEAR },
  );
  return frames;
}

function piece(name: LogoPiece) {
  return {
    origin: PIECE_ORIGIN[name],
    transform: transformTrack(name),
    opacity: opacityTrack(name),
  };
}

export const LOGO_LOADER: LogoLoaderTimeline = {
  durationMs: 4000,
  restOffsets: [GIRO_END, 1],
  spin: [
    { offset: 0, rotateDeg: 0, easing: LINEAR },
    { offset: 0.0775, rotateDeg: 0, easing: SPIN },
    { offset: 0.407, rotateDeg: 360, easing: LINEAR },
    { offset: 1, rotateDeg: 360, easing: LINEAR },
  ],
  pieces: {
    top: piece("top"),
    bottomLeft: piece("bottomLeft"),
    right: piece("right"),
  },
};

const COMPACT_SPLIT_SCALE = 0.88;

function compactPiece(name: LogoPiece) {
  const [sx, sy] = SPLIT_OFFSET[name];
  const rest = { x: 0, y: 0, scale: 1 };
  const split = { x: sx, y: sy, scale: COMPACT_SPLIT_SCALE };
  return {
    origin: PIECE_ORIGIN[name],
    transform: [
      { offset: 0, ...rest, easing: SPLIT_OUT },
      { offset: 0.16, ...split, easing: LINEAR },
      { offset: 0.58, ...split, easing: SNAP_BACK },
      { offset: 0.84, ...rest, easing: LINEAR },
      { offset: 1, ...rest, easing: LINEAR },
    ],
    opacity: [
      { offset: 0, opacity: 1, easing: LINEAR },
      { offset: 1, opacity: 1, easing: LINEAR },
    ],
  };
}

/**
 * Variante compacta, para botão e ícone até 24px (TAM-281): só o Giro, em
 * 1,2 s, sem a Onda — num botão a ação costuma acabar antes de o ciclo de 4 s
 * chegar à Onda, e em 16px a diferença de opacidade nem se lê. Protótipo na
 * seção "Variante compacta" de `docs/design/loader-logo/opcao-7.html`.
 */
export const LOGO_LOADER_COMPACT: LogoLoaderTimeline = {
  durationMs: 1200,
  /** As peças terminam de voltar ao lugar em 0,84; dali ao fim, paradas. */
  restOffsets: [0.84, 1],
  spin: [
    { offset: 0, rotateDeg: 0, easing: LINEAR },
    { offset: 0.08, rotateDeg: 0, easing: [0.65, 0, 0.25, 1] },
    { offset: 0.78, rotateDeg: 360, easing: LINEAR },
    { offset: 1, rotateDeg: 360, easing: LINEAR },
  ],
  pieces: {
    top: compactPiece("top"),
    bottomLeft: compactPiece("bottomLeft"),
    right: compactPiece("right"),
  },
};

/** `[0.3, 0, 0.2, 1]` → `"cubic-bezier(0.3, 0, 0.2, 1)"` */
export function bezierCss([x1, y1, x2, y2]: BezierCurve): string {
  return `cubic-bezier(${x1}, ${y1}, ${x2}, ${y2})`;
}

/**
 * Transição da splash nativa para o loader, no app (TAM-283). Protótipo em
 * `docs/design/loader-logo/splash.html`.
 *
 * A splash nativa é a imagem do ícone em quadrado (`app.json`, 180pt). A
 * camada animada começa idêntica a ela, o quadrado encolhe até envolver as
 * peças, se dissolve, e o `LOGO_LOADER` assume. Tempos em ms desde que a
 * camada aparece.
 */
export const SPLASH_TRANSITION = {
  /** Lado do ícone na splash nativa — o `imageWidth` do `app.json`. */
  iconSizePt: 180,
  /** Largura das peças dentro do ícone: 280 de 512 px na imagem. */
  piecesWidthPt: 98.4,
  /** Raio do quadrado, com canto contínuo: 86 de 512 px na imagem. */
  tileRadiusPt: 30.2,
  /** A imagem da splash some por cima da versão vetorial (esconde o canto). */
  imageFade: { startMs: 100, durationMs: 150 },
  /** O quadrado encolhe até as peças e elas assumem a cor dele. */
  condense: { startMs: 150, durationMs: 500, easing: [0.65, 0, 0.35, 1] as BezierCurve },
  /** As peças claras somem por cima do quadrado (viram a cor dele). */
  piecesToTile: { startMs: 150, durationMs: 400 },
  /** O quadrado se dissolve e as peças na cor do tema aparecem. */
  dissolve: { startMs: 550, durationMs: 250 },
  /** O Giro → Onda começa; antes disso a camada não sai. */
  loopStartMs: 800,
  /*
   * A camada só sai num `LOGO_LOADER.restOffsets` em que o app já está pronto
   * — nunca no meio do movimento. O primeiro é o fim do primeiro Giro: a Onda
   * não precisa aparecer (pedido do dono, 2026-10-05), e abrir o app leva ao
   * menos `loopStartMs` + o Giro, cerca de 2,6 s.
   */
  /** Fade de saída, no ponto de pausa em que o app já estava pronto. */
  exitMs: 350,
} as const;
